// Google Picker for Penyimpanan Pluto: the admin picks the Drive folder that
// holds the users' folders, as the central account. The API hands a short-lived
// drive.file access token of that account, the Picker API key and the Cloud
// project number; picking a folder is what grants the account (scope
// drive.file) access to a folder it did not create. The loader script comes
// from apis.google.com (allowed in apps/web/server.ts CSP) and is added only
// when the admin presses Pilih folder.

const SCRIPT_URL = "https://apis.google.com/js/api.js";
const FOLDER_MIME = "application/vnd.google-apps.folder";

/* Minimal shapes of the parts of gapi and google.picker used here. */
interface PickerDoc {
  id: string;
  name?: string;
}
interface PickerData {
  action: string;
  docs?: PickerDoc[];
}
interface PickerNamespace {
  Action: { PICKED: string; CANCEL: string };
  ViewId: { FOLDERS: string };
  Feature: { SUPPORT_DRIVES: string };
  DocsView: new (viewId: string) => {
    setIncludeFolders(on: boolean): unknown;
    setSelectFolderEnabled(on: boolean): unknown;
    setMimeTypes(types: string): unknown;
  };
  PickerBuilder: new () => PickerBuilder;
}
interface PickerBuilder {
  addView(view: unknown): PickerBuilder;
  enableFeature(feature: string): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(appId: string): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  setCallback(cb: (data: PickerData) => void): PickerBuilder;
  build(): { setVisible(on: boolean): void };
}
type GoogleWindow = Window & {
  gapi?: { load(name: string, options: { callback: () => void; onerror: () => void }): void };
  google?: { picker?: PickerNamespace };
};

let loading: Promise<PickerNamespace> | null = null;

function loadPicker(): Promise<PickerNamespace> {
  const w = window as GoogleWindow;
  if (w.google?.picker) return Promise.resolve(w.google.picker);
  loading ??= new Promise<PickerNamespace>((resolve, reject) => {
    const fail = () => {
      loading = null;
      reject(new Error("Pemilih folder Google tidak dapat dimuat. Periksa koneksi, lalu coba lagi."));
    };
    const ready = () =>
      w.gapi?.load("picker", {
        callback: () => (w.google?.picker ? resolve(w.google.picker) : fail()),
        onerror: fail,
      });
    if (w.gapi) {
      ready();
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = ready;
    script.onerror = fail;
    document.head.appendChild(script);
  });
  return loading;
}

/** Opens Google Picker on folders; the picked folder, or null when the admin closes it. */
async function pickFolder(config: { accessToken: string; apiKey: string; appId: string }): Promise<{ id: string; name: string } | null> {
  const g = await loadPicker();
  return new Promise((resolve) => {
    const view = new g.DocsView(g.ViewId.FOLDERS);
    view.setIncludeFolders(true);
    view.setSelectFolderEnabled(true);
    view.setMimeTypes(FOLDER_MIME);
    const picker = new g.PickerBuilder()
      .addView(view)
      .enableFeature(g.Feature.SUPPORT_DRIVES)
      .setOAuthToken(config.accessToken)
      .setDeveloperKey(config.apiKey)
      .setAppId(config.appId)
      .setTitle("Pilih folder data pengguna")
      .setCallback((data) => {
        if (data.action === g.Action.PICKED && data.docs?.[0]) resolve({ id: data.docs[0].id, name: data.docs[0].name ?? "" });
        else if (data.action === g.Action.CANCEL) resolve(null);
      })
      .build();
    picker.setVisible(true);
  });
}

/** Seam for tests (no Google in happy-dom). */
export const googlePicker = { pickFolder };
